import type { ReportChart, ReportDocument, ResultSnapshot, TableData } from './files';
import type { AgentSkill } from './skills';
import type { DocumentLayout } from './task';
import type { Brief } from './brief';
import { withThousands } from './korean';
import { reportFigures } from './reportHtml';

/** 과제 단계가 정한 분량과, 그에 맞춰 미리 만든 것들. */
export type ReportExtra = {
  layout?: DocumentLayout;
  charts?: ReportChart[];
  brief?: Brief;
  basis?: string;
  statusRule?: string;
  statusTable?: TableData;
  statusNote?: string;
  lineNotes?: ReportDocument['lineNotes'];
  axes?: ReportDocument['axes'];
  dataNotes?: string[];
  population?: TableData;
  memos?: ReportDocument['memos'];
  exceptions?: ReportDocument['exceptions'];
  /** 점검이 아닌 목록의 표(부서별 현황, 상위 명단). */
  tables?: ReportDocument['tables'];
  /** 그 표의 핵심 숫자(인원·합계). 있으면 계산 요약 대신 이것이 숫자 자리에 오른다. */
  figures?: ReportDocument['kpis'];
  genre?: ReportDocument['genre'];
  /** 로그인한 사람의 부서(공지의 발신·문의처). */
  unit?: string;
  /** 문서 표(엑셀로 받는 명단)를 바꿔 쓸 때. 연차 목록은 대상 여부 칸을 더한 명단이다. */
  table?: TableData;
  /** 직전 답. 작성 호출이 실패하면 이것의 요지가 요약이 된다. */
  answer?: string;
  /** 이 문서를 쓴 자료 읽기. 문서 턴에 모델이 읽었으면 다음 문서 턴이 다시 묻지 않고 같은 뜻으로 쓴다. */
  reading?: ReportDocument['reading'];
};

/**
 * 직전 답의 요지 — 단추 안내·되묻는 말을 뺀 앞 두 문단.
 * 고정 문구 「확정된 ERP 계산 결과를 기준으로 현황을 정리했습니다」가 요약 자리를 채웠다(2026-10-23).
 */
export function answerGist(answer?: string): string | undefined {
  const kept = (answer ?? '').split(/\n\s*\n/)
    .map((block) => block.split('\n').filter((line) => !/엑셀로 받기|준비했습니다|(?:버튼|단추)으로 받아|볼까요\?|할까요\?|^\s*(맞아요|틀려요)\s*$/.test(line))
      .join('\n').trim())
    .filter(Boolean)
    .slice(0, 2);
  return kept.length ? kept.join('\n\n') : undefined;
}

type LayoutFields = Pick<ReportDocument,
  'headline' | 'points' | 'charts' | 'detailRows' | 'basis' | 'statusTable' | 'statusNote' | 'exceptions' | 'statusRule'
  | 'lineNotes' | 'axes' | 'dataNotes' | 'population' | 'memos' | 'tables' | 'genre' | 'unit' | 'reading' | 'deck'>
  & { title?: string };

/**
 * 분량(layout)에 따른 칸. 「한 장」(top)은 원자료 표 없이 요약표·예외 행, 「표 빼고」(none)는 표 없이,
 * 「전체 목록」(all)은 원자료 전부를 별첨으로.
 */
function layoutFields(extra: ReportExtra | undefined): LayoutFields {
  if (!extra?.layout) return {};
  const detail = extra.layout.detail;
  const brief = extra.brief;
  const messages = brief?.charts ?? [];
  return {
    ...(brief ? {
      title: brief.title,
      headline: brief.summary[0],
      points: { summary: brief.summary, checks: brief.checks, actions: brief.actions },
    } : {}),
    ...(extra.basis ? { basis: extra.basis } : {}),
    ...(extra.statusRule ? { statusRule: extra.statusRule } : {}),
    ...(extra.lineNotes?.length ? { lineNotes: extra.lineNotes } : {}),
    ...(extra.axes?.length ? { axes: extra.axes } : {}),
    ...(extra.dataNotes?.length ? { dataNotes: extra.dataNotes } : {}),
    ...(extra.population ? { population: extra.population } : {}),
    ...(extra.memos?.length ? { memos: extra.memos } : {}),
    ...(extra.genre ? { genre: extra.genre } : {}),
    ...(extra.unit ? { unit: extra.unit } : {}),
    ...(extra.reading ? { reading: extra.reading } : {}),
    // 발표자료 꼴은 과제 단계가 사용자 말로 고른다(마케팅형). 말이 없으면 보고용이다.
    ...(extra.layout.style === 'keynote' ? { deck: 'keynote' as const } : {}),
    // 목록 표는 「한 장」에도 싣는다. 부서별 현황과 상위 명단은 원자료 표와 달리 코드가 센 요약이다. 「표 빼고」면 뺀다.
    ...(detail !== 'none' && extra.tables?.length ? { tables: extra.tables } : {}),

    ...(extra.layout.charts && extra.charts?.length ? {
      charts: extra.charts.map((chart, index) => (messages[index] ? { ...chart, message: messages[index] } : chart)),
    } : {}),
    ...(detail !== 'none' && extra.statusTable ? { statusTable: extra.statusTable } : {}),
    ...(detail !== 'none' && extra.statusTable && extra.statusNote ? { statusNote: extra.statusNote } : {}),
    ...(detail !== 'none' && extra.exceptions ? { exceptions: extra.exceptions } : {}),
    ...(detail === 'all' ? {} : { detailRows: 0 }),
  };
}

function periodOf(question: string, createdAt: string, cadence?: 'weekly' | 'monthly'): string {
  const explicit = question.match(/(20\d{2})\s*년\s*(1[0-2]|0?[1-9])\s*월/);
  if (explicit) return `${explicit[1]}년 ${Number(explicit[2])}월`;
  const date = new Date(createdAt);
  if (cadence === 'weekly' || /(?:이번|지난)\s*주/.test(question)) {
    const mondayOffset = (date.getDay() + 6) % 7;
    const start = new Date(date);
    start.setDate(date.getDate() - mondayOffset - (/지난\s*주/.test(question) ? 7 : 0));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
    const startText = `${start.getFullYear()}년 ${start.getMonth() + 1}월 ${start.getDate()}일`;
    const endText = sameMonth
      ? `${end.getMonth() + 1}월 ${end.getDate()}일`
      : `${end.getFullYear()}년 ${end.getMonth() + 1}월 ${end.getDate()}일`;
    return `${startText} ~ ${endText}`;
  }
  if (cadence === 'monthly' || /이번\s*달/.test(question)) {
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
  }
  // 사람이 자주 쓰는 말을 더 안다. 모르는 말이면 **기간을 지어내지 않는다** —
  // 예전에는 `조회 기준 기간 ERP 현황 보고서` 라는 제목이 그대로 나갔다.
  if (/지난\s*달|전월/.test(question)) {
    const last = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    return `${last.getFullYear()}년 ${last.getMonth() + 1}월`;
  }
  if (/분기/.test(question)) {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const past = /지난\s*분기|전분기/.test(question);
    const year = past && quarter === 1 ? date.getFullYear() - 1 : date.getFullYear();
    const shown = past ? (quarter === 1 ? 4 : quarter - 1) : quarter;
    return `${year}년 ${shown}분기`;
  }
  if (/작년|지난\s*해/.test(question)) return `${date.getFullYear() - 1}년`;
  if (/올해|금년/.test(question)) return `${date.getFullYear()}년`;
  return '';
}

/** 제목은 기간을 모를 때 그 자리를 비운다. `조회 기준 기간 …` 은 사람 말이 아니다. */
function titled(period: string, name: string): string {
  return period ? `${period} ${name}` : name;
}

function displayValue(label: string, value: unknown): string {
  const text = withThousands(String(value ?? ''));
  if (/휴가자.*수|인원|명수/.test(label) && /^[\d,]+(?:\.\d+)?$/.test(text)) return `${text}명`;
  if (/일수/.test(label) && /^[\d,]+(?:\.\d+)?$/.test(text)) return `${text}일`;
  return text;
}

/**
 * 문서 파일(엑셀 포함)에 딸린 표. 공지는 부서별 표뿐이다. 공지의 엑셀 받기가 74명 이름과 잔여 일수를 내보냈다(국가직 팀장 검토,
 * 연차 공지). 부서별 표가 없는 공지는 표가 없다.
 */
function documentTable(table: TableData | undefined, extra: ReportExtra | undefined): TableData | undefined {
  if (extra?.genre !== 'notice') return table;
  return extra.tables?.find((one) => one.role === 'groups')?.table;
}

/** 사람이 읽을 수 있는 값인가. 객체·배열은 문장에 넣으면 `[object Object]` 가 된다. */
function readable(value: unknown): boolean {
  return value !== null && value !== undefined && typeof value !== 'object';
}

function kpisOf(summary: unknown): { label: string; value: string }[] {
  if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
    // **읽을 수 없는 값은 뺀다.** 계산이 중첩된 객체를 돌려주면 그것이 그대로
    // 사무직이 읽는 문장에 실렸다 — `핵심 집계는 summary [object Object], rows 입니다`.
    // 뺄 것을 빼고 남는 것이 없으면 집계 문장 자체를 만들지 않는 편이 낫다.
    return Object.entries(summary as Record<string, unknown>)
      .filter(([, value]) => readable(value))
      .slice(0, 6)
      .map(([label, value]) => ({ label, value: displayValue(label, value) }));
  }
  // 문자열 요약은 그 자체가 문장이다. `결과` 라는 라벨을 붙이면
  // `핵심 집계는 결과 202608월 …입니다` 처럼 우리 쪽 말이 문장에 섞인다.
  return readable(summary) ? [{ label: '', value: withThousands(String(summary)) }] : [];
}

export function makeReport(snapshot: ResultSnapshot, skill?: AgentSkill, extra?: ReportExtra): ReportDocument {
  const profile = skill?.reportProfile;
  const period = periodOf(snapshot.question, snapshot.createdAt, profile?.cadence);
  // 보고 문서에는 짧은 숫자 값만 — 「…분류했습니다.」 같은 서술 문장이 데이터에 남았다(국가직 팀장 3차 검토).
  // 목록 표가 있으면 그 표의 인원·합계가 숫자 자리에 오른다. 계산 요약 앞 여섯 칸은 「조회연도 2,026」, 「재직조건 재직자」였다(실측 2026-10-26).
  // 목록 표가 없는 공지는 빈 숫자 자리(figures: [])를 받는다. 계산 요약에는 사람 이름 값이 있을 수 있다.
  const kpis = extra?.figures ? extra.figures
    : extra?.layout ? reportFigures(kpisOf(snapshot.summary)) : kpisOf(snapshot.summary);
  const vacation = /휴가/.test(snapshot.question) || kpis.some((kpi) => /휴가/.test(kpi.label));
  const kpiSummary = kpis.slice(0, 3)
    .map((kpi) => (kpi.label ? `${kpi.label} ${kpi.value}` : kpi.value)).join(', ');
  // 공지는 작성이 실패해도 「보고서」 제목으로 내지 않고, 요약 자리에 자기 보증 글(「확정된 ERP 계산 결과를 …」)을 쓰지 않는다.
  const kindName = extra?.genre === 'notice' ? '안내' : '보고서';
  const notice = extra?.genre === 'notice';
  return {
    title: profile ? titled(period, profile.title)
      : vacation ? titled(period, `휴가자 현황 ${kindName}`) : titled(period, `ERP 현황 ${kindName}`),
    period,
    audience: profile?.audience,
    cadence: profile?.cadence === 'weekly' ? '주간' : profile?.cadence === 'monthly' ? '월간' : undefined,
    summaryHeading: profile?.summaryHeading,
    detailHeading: profile?.detailHeading,
    accent: profile?.accent,
    kpis,
    summary: extra?.brief ? extra.brief.summary.join('\n') : notice ? answerGist(extra?.answer) ?? '' : (profile
      ? `${profile.purpose}${kpiSummary ? ` 핵심 집계는 ${kpiSummary}입니다.` : ''}`
      : vacation && kpis.length >= 2
      ? `${period} 휴가자는 ${kpis[0]?.value}이며, 휴가일수 합계는 ${kpis[1]?.value}로 집계되었습니다.`
      : answerGist(extra?.answer) ?? '확정된 ERP 계산 결과를 기준으로 현황을 정리했습니다.'),
    ...layoutFields(extra),
    table: documentTable(extra?.table ?? snapshot.table, extra),
    // 분량을 정한 보고 문서에는 쓰지 않는다 — 「확정된·검증된」은 자기 보증이다(국가직 팀장 검토, 2026-10-23).
    methodology: extra?.layout ? '' : `확정된 ERP 조회 결과와 검증된 계산 규칙을 적용했습니다.${profile ? ` ${profile.audience} ${profile.cadence === 'weekly' ? '주간' : '월간'} 보고 형식으로 구성했습니다.` : ''}`,
    sources: snapshot.sources,
    generatedAt: new Date().toISOString(),
    style: skill?.style,
  };
}

export function fallbackReport(
  answer: string,
  table: TableData | undefined,
  question: string,
  skill?: AgentSkill,
  extra?: ReportExtra,
): ReportDocument {
  const generatedAt = new Date().toISOString();
  const profile = skill?.reportProfile;
  const period = periodOf(`${question}\n${answer}`, generatedAt, profile?.cadence);
  return {
    title: profile ? titled(period, profile.title) : titled(period, 'ERP 현황 보고서'),
    period, audience: profile?.audience,
    cadence: profile?.cadence === 'weekly' ? '주간' : profile?.cadence === 'monthly' ? '월간' : undefined,
    summaryHeading: profile?.summaryHeading, detailHeading: profile?.detailHeading,
    accent: profile?.accent, kpis: extra?.figures ?? [],
    // 공지는 작성이 실패해도 앞 답(실명이 든)을 싣지 않는다. 이름을 지운 글의 요지만. 보고서도 넘겨받은 요지가 있으면 그것이다.
    // 계정을 뺀 연차 보고서에 앞 답(뺀 계정이 든 수)이 실렸다(독립 검토).
    summary: extra?.brief ? extra.brief.summary.join('\n') : extra?.genre === 'notice' ? answerGist(extra.answer) ?? ''
      : answerGist(extra?.answer) ?? answer,
    table: documentTable(extra?.table ?? table, extra),
    ...layoutFields(extra),
    methodology: '대화에서 확정된 결과를 재조회하지 않고 사용했습니다.',
    sources: [], generatedAt, style: skill?.style,
  };
}
